import { useEffect, useRef } from "react";

/**
 * Runs `onTick` every `intervalMs` while `enabled` (sc-100769).
 *
 * Contract:
 * - **never overlaps within one armed interval** — a tick whose previous call is still in flight is
 *   skipped. It is deliberately NOT a promise that two `onTick` bodies can never be live at once: this
 *   hook cannot cancel `onTick`, so when `enabled` goes false the cleanup abandons an unsettled call and
 *   releases the guard (otherwise a call that never settles would leave the next armed interval skipping
 *   every tick). A caller whose `onTick` must not run concurrently across an enabled cycle has to guard
 *   it itself — both of this story's consumers do: the recurring page's `refresh` is single-flight, and
 *   the runs tabs' is bounded and last-write-wins ordered, so no *request* can stack up either way,
 *   which is what the acceptance criterion actually forbids. [code review: CodeRabbit on MR !205]
 * - **bounds its own overlap guard** — a tick that never *settles* would otherwise hold the guard for
 *   the rest of the mount, and cleanup only runs when `enabled` flips or the component unmounts, which
 *   never happens while a frozen non-terminal row keeps polling enabled. After `maxInFlightMs` the
 *   stuck call is abandoned and ticking resumes; its late settle is ignored. [code review r2]
 * - clears its interval on unmount and whenever `enabled` goes false (a route element unmounts on
 *   navigation away, so that covers navigation too), releasing the guard with it;
 * - `onTick` owns its own error handling: this hook never toasts and never re-throws, so a rejecting
 *   tick cannot surface as an unhandled rejection.
 */
export function usePollWhile({
  enabled,
  intervalMs,
  onTick,
  maxInFlightMs,
}: {
  enabled: boolean;
  intervalMs: number;
  onTick: () => Promise<void>;
  /** Defaults to 10 intervals — the same "10 cycles" bound the page uses for a stuck request. */
  maxInFlightMs?: number;
}): void {
  const inFlightRef = useRef(false);
  const inFlightSinceRef = useRef(0);
  // Only the tick that still owns the guard may release it, so a late settle from an abandoned call
  // cannot clear a newer tick's guard and admit a genuine overlap.
  const tickRef = useRef(0);
  const onTickRef = useRef(onTick);

  // Latest-callback ref, so a new onTick identity does not re-arm the interval mid-cycle.
  useEffect(() => {
    onTickRef.current = onTick;
  }, [onTick]);

  useEffect(() => {
    if (!enabled) return;

    const bound = maxInFlightMs ?? intervalMs * 10;

    const id = window.setInterval(() => {
      const now = Date.now();
      if (inFlightRef.current && now - inFlightSinceRef.current < bound) return;

      const tick = ++tickRef.current; // abandons any stuck predecessor
      inFlightRef.current = true;
      inFlightSinceRef.current = now;
      const settle = () => {
        if (tick !== tickRef.current) return;
        inFlightRef.current = false;
      };
      void Promise.resolve(onTickRef.current()).then(settle, settle);
    }, intervalMs);

    return () => {
      window.clearInterval(id);
      // Release the overlap guard with the interval that set it. A tick whose onTick never SETTLES leaves
      // it true, and a guard that outlives its interval makes the next armed interval skip every tick.
      inFlightRef.current = false;
    };
  }, [enabled, intervalMs, maxInFlightMs]);
}
